import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import Table from '../../../components/Table/Table';
import Button from '../../../components/Button/Button';
import { Modal } from '../../../components/Modal/Modal';
import AlertModal from '../../../components/AlertModal/AlertModal';
import { DynamicInput, DynamicSelect } from '../../../components/Inputs/Inputs';
import { extractApiError } from '../../../utils/normalizeApiError';
import './ConciergeAssistant.scss';

// Assistente IA do Concierge (SuperAdmin) — credencial de cada instalação do Concierge que
// usa o motor de IA. A licença do módulo CONCIERGE_ASSISTANT gere-se em Licenciamento; aqui
// cria-se a chave que vai para o .env do Concierge (ASSISTANT_ENGINE_KEY). A chave só é
// mostrada ao criar ou ao gerar uma nova — não volta a aparecer.
const MODULE_CODE = 'CONCIERGE_ASSISTANT';
const EMPTY_FORM = { hotel_id: '', name: '', concierge_url: '' };

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('pt-PT');
}

export default function ConciergeAssistant() {
  const { axiosAuth } = useContext(AuthContext);
  const [installations, setInstallations] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [editing, setEditing] = useState(null); // instalação a editar
  const [editForm, setEditForm] = useState({ name: '', concierge_url: '' });

  const [newKey, setNewKey] = useState(null); // { name, api_key }
  const [copied, setCopied] = useState(false);
  const [usage, setUsage] = useState(null); // { installation, data }
  const [confirmRotate, setConfirmRotate] = useState(null);

  const [alert, setAlert] = useState({ open: false, title: '', message: '' });
  const showError = useCallback((title, err) => {
    setAlert({ open: true, title, message: extractApiError(err) });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: inst }, { data: hotelsData }] = await Promise.all([
        axiosAuth.get('/concierge-installations'),
        axiosAuth.get('/hotels'),
      ]);
      setInstallations(inst || []);
      setHotels(hotelsData || []);
    } catch (err) {
      showError('Erro ao carregar as instalações', err);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, showError]);

  useEffect(() => {
    load();
  }, [load]);

  // só hotéis que ainda não têm instalação (1 por hotel)
  const hotelOptions = useMemo(() => {
    const used = new Set(installations.map((i) => i.hotel_id));
    return hotels
      .filter((h) => !used.has(h.id))
      .map((h) => ({ value: String(h.id), label: h.name }));
  }, [hotels, installations]);

  const handleCreate = useCallback(async () => {
    if (!form.hotel_id || !form.name.trim()) {
      setAlert({ open: true, title: 'Dados em falta', message: 'Escolha o hotel e indique um nome.' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await axiosAuth.post('/concierge-installations', {
        hotel_id: Number(form.hotel_id),
        name: form.name.trim(),
        concierge_url: form.concierge_url.trim() || null,
      });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setCopied(false);
      setNewKey({ name: data.name, api_key: data.api_key, licensed: data.licensed });
      load();
    } catch (err) {
      showError('Erro ao criar a instalação', err);
    } finally {
      setSaving(false);
    }
  }, [axiosAuth, form, load, showError]);

  const handleSaveEdit = useCallback(async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axiosAuth.patch(`/concierge-installations/${editing.id}`, {
        name: editForm.name.trim(),
        concierge_url: editForm.concierge_url.trim() || null,
      });
      setEditing(null);
      load();
    } catch (err) {
      showError('Erro ao guardar', err);
    } finally {
      setSaving(false);
    }
  }, [axiosAuth, editForm, editing, load, showError]);

  const toggleActive = useCallback(
    async (row) => {
      setBusyId(row.id);
      try {
        await axiosAuth.patch(`/concierge-installations/${row.id}`, { active: !row.active });
        load();
      } catch (err) {
        showError('Erro ao alterar o estado', err);
      } finally {
        setBusyId(null);
      }
    },
    [axiosAuth, load, showError],
  );

  const rotateKey = useCallback(async () => {
    const row = confirmRotate;
    setConfirmRotate(null);
    if (!row) return;
    setBusyId(row.id);
    try {
      const { data } = await axiosAuth.post(`/concierge-installations/${row.id}/rotate-key`);
      setCopied(false);
      setNewKey({ name: data.name, api_key: data.api_key, licensed: data.licensed });
      load();
    } catch (err) {
      showError('Erro ao gerar a chave', err);
    } finally {
      setBusyId(null);
    }
  }, [axiosAuth, confirmRotate, load, showError]);

  const openUsage = useCallback(
    async (row) => {
      setBusyId(row.id);
      try {
        const { data } = await axiosAuth.get(`/concierge-installations/${row.id}/usage`, { params: { days: 30 } });
        setUsage({ installation: row, data });
      } catch (err) {
        showError('Erro ao carregar o consumo', err);
      } finally {
        setBusyId(null);
      }
    },
    [axiosAuth, showError],
  );

  const copyKey = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(newKey?.api_key || '');
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [newKey]);

  const columns = useMemo(
    () => [
      { key: 'name', label: 'Instalação' },
      { key: 'hotel', label: 'Hotel / Grupo' },
      {
        key: 'concierge_url',
        label: 'Concierge',
        render: (row) =>
          row.concierge_url ? (
            <a href={row.concierge_url} target="_blank" rel="noreferrer">
              {row.concierge_url.replace(/^https?:\/\//, '')}
            </a>
          ) : (
            '—'
          ),
      },
      {
        key: 'licensed',
        label: 'Licença',
        render: (row) => (
          <span className={`ca-badge ${row.licensed ? 'on' : 'off'}`}>{row.licensed ? 'Ativa' : 'Sem licença'}</span>
        ),
      },
      {
        key: 'active',
        label: 'Chave',
        render: (row) => (
          <span className={`ca-badge ${row.active ? 'on' : 'off'}`}>
            {row.active ? row.key_id : 'Desativada'}
          </span>
        ),
      },
      { key: 'last_seen', label: 'Último contacto', render: (row) => formatDate(row.last_seen_at) },
      {
        key: 'actions',
        label: '',
        render: (row) => {
          const busy = busyId === row.id || loading;
          return (
            <div className="ca-actions">
              <Button variant="secondary" disabled={busy} onClick={() => openUsage(row)}>
                Consumo
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setEditing(row);
                  setEditForm({ name: row.name, concierge_url: row.concierge_url || '' });
                }}
              >
                Editar
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => setConfirmRotate(row)}>
                Nova chave
              </Button>
              <Button variant={row.active ? 'danger' : 'primary'} disabled={busy} onClick={() => toggleActive(row)}>
                {row.active ? 'Desativar' : 'Ativar'}
              </Button>
            </div>
          );
        },
      },
    ],
    [busyId, loading, openUsage, toggleActive],
  );

  const tableData = useMemo(
    () =>
      installations.map((i) => ({
        ...i,
        hotel: [i.hotel_name, i.group_name].filter(Boolean).join(' / '),
      })),
    [installations],
  );

  return (
    <div className="concierge-assistant-page">
      {alert.open && (
        <AlertModal
          variant="danger"
          title={alert.title || 'Ocorreu um erro'}
          message={alert.message}
          onClose={() => setAlert({ open: false, title: '', message: '' })}
        />
      )}

      <PageTitle link="" title="Assistente IA do Concierge" />

      <div className="ca-hint">
        <p>
          O Assistente IA é vendido só com o Concierge. Para ativar um hotel: (1) criar o grupo e o hotel em
          Grupos/Hotéis, (2) ligar o módulo <strong>{MODULE_CODE}</strong> em{' '}
          <Link to="/licensing">Licenciamento</Link>, (3) criar aqui a instalação e colocar a chave no{' '}
          <code>.env</code> do Concierge (<code>ASSISTANT_ENGINE_KEY</code>). Tudo o resto — configuração,
          conversa, pedidos e dashboard — faz-se no Backoffice do Concierge.
        </p>
      </div>

      <div className="ca-toolbar">
        <Button onClick={() => setCreateOpen(true)} disabled={loading}>
          Nova instalação
        </Button>
      </div>

      <div className="container">
        {installations.length === 0 && !loading ? (
          <p className="ca-empty">Ainda não há instalações do Concierge com Assistente IA.</p>
        ) : (
          <Table columns={columns} data={tableData} />
        )}
      </div>

      <Modal
        isVisible={createOpen}
        title="Nova instalação do Concierge"
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
        saveText="Criar e gerar chave"
        disableActions={saving}
      >
        <div className="ca-form">
          <DynamicSelect
            label="Hotel"
            value={form.hotel_id}
            options={hotelOptions}
            placeholder={hotelOptions.length ? 'Escolha o hotel' : 'Todos os hotéis já têm instalação'}
            onChange={(e) => setForm((f) => ({ ...f, hotel_id: e.target.value }))}
          />
          <DynamicInput
            label="Nome"
            name="name"
            value={form.name}
            maxLength={150}
            placeholder="Ex.: Concierge — Hotel Exemplo"
            onChange={(_n, v) => setForm((f) => ({ ...f, name: v }))}
          />
          <DynamicInput
            label="Endereço do Concierge"
            name="concierge_url"
            value={form.concierge_url}
            maxLength={255}
            placeholder="https://concierge.hoteisdigitais.com"
            onChange={(_n, v) => setForm((f) => ({ ...f, concierge_url: v }))}
          />
        </div>
      </Modal>

      <Modal
        isVisible={Boolean(editing)}
        title="Editar instalação"
        onClose={() => setEditing(null)}
        onSave={handleSaveEdit}
        disableActions={saving}
      >
        <div className="ca-form">
          <DynamicInput
            label="Nome"
            name="edit-name"
            value={editForm.name}
            maxLength={150}
            onChange={(_n, v) => setEditForm((f) => ({ ...f, name: v }))}
          />
          <DynamicInput
            label="Endereço do Concierge"
            name="edit-url"
            value={editForm.concierge_url}
            maxLength={255}
            onChange={(_n, v) => setEditForm((f) => ({ ...f, concierge_url: v }))}
          />
        </div>
      </Modal>

      <Modal
        isVisible={Boolean(confirmRotate)}
        title="Gerar chave nova?"
        onClose={() => setConfirmRotate(null)}
        onSave={rotateKey}
        saveText="Gerar chave nova"
      >
        <p>
          A chave atual de <strong>{confirmRotate?.name}</strong> deixa de funcionar de imediato. O assistente fica
          indisponível nesse Concierge até a chave nova ser colocada no <code>.env</code>.
        </p>
      </Modal>

      <Modal
        isVisible={Boolean(newKey)}
        title="Chave da instalação"
        onClose={() => setNewKey(null)}
        disableBackdropClose
      >
        <div className="ca-key">
          <p>
            Copie esta chave para o <code>.env</code> do Concierge de <strong>{newKey?.name}</strong>. Não volta a ser
            mostrada.
          </p>
          <pre>ASSISTANT_ENGINE_KEY={newKey?.api_key}</pre>
          <Button variant="secondary" onClick={copyKey}>
            {copied ? 'Copiada' : 'Copiar chave'}
          </Button>
          {newKey && !newKey.licensed && (
            <p className="ca-warn">
              Este hotel ainda não tem a licença {MODULE_CODE} ativa — o Concierge não mostra o assistente até a
              licença ser ligada em Licenciamento.
            </p>
          )}
        </div>
      </Modal>

      <Modal isVisible={Boolean(usage)} title={`Consumo — ${usage?.installation?.name || ''}`} onClose={() => setUsage(null)}>
        {usage && (
          <div className="ca-usage">
            <p className="ca-muted">Últimos {usage.data.days} dias (desde {usage.data.since}).</p>
            <div className="ca-usage-grid">
              <div>
                <span>Pedidos</span>
                <strong>{formatNumber(usage.data.totals.requests)}</strong>
              </div>
              <div>
                <span>Erros</span>
                <strong>{formatNumber(usage.data.totals.errors)}</strong>
              </div>
              <div>
                <span>Tokens (entrada)</span>
                <strong>{formatNumber(usage.data.totals.tokens_in)}</strong>
              </div>
              <div>
                <span>Tokens (saída)</span>
                <strong>{formatNumber(usage.data.totals.tokens_out)}</strong>
              </div>
              <div>
                <span>Custo estimado</span>
                <strong>
                  {usage.data.totals.estimated_cost === null || usage.data.totals.estimated_cost === undefined
                    ? 'Preço não configurado'
                    : `${usage.data.totals.estimated_cost.toFixed(2)} ${usage.data.currency}`}
                </strong>
              </div>
            </div>
            {usage.data.daily.length > 0 && (
              <table className="ca-usage-table">
                <thead>
                  <tr>
                    <th>Dia</th>
                    <th>Pedidos</th>
                    <th>Erros</th>
                    <th>Tokens</th>
                    <th>Latência média</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.data.daily.map((d) => (
                    <tr key={d.day}>
                      <td>{d.day}</td>
                      <td>{formatNumber(d.requests)}</td>
                      <td>{formatNumber(d.errors)}</td>
                      <td>{formatNumber(d.tokens_in + d.tokens_out)}</td>
                      <td>{d.avg_latency_ms ? `${d.avg_latency_ms} ms` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
